import { Observable, of, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';

import { HttpCacheService } from '@app-core/services/cache/cache.service';
import { API } from '@app-core/constants/api.constants';
import { Notification_Configuration } from '../models/notification-configuration.model';
import {
  ORDER_NOTIFICATION_CONFIGURATION,
  INPUT_KEYS,
  EVENTS_DEFAULT_VALUES,
  DIAGNOSTIC_EVENTS,
  SAFETY_EVENTS,
  OTHER_EVENTS,
  DISTRACTED_EVENTS,
} from '../constants/constants';
import { SsoDialogComponent } from '@app-shared/components/dialog/sso-dialog/sso-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { DurationRangeService } from '@app-core/services/duration-range/duration-range.service';
import { PortalNotificationEventFlag } from '@app-core/models/core.model';
@Injectable({
  providedIn: 'root',
})
export class NotificationSettingService {
  private _exposedEvents: PortalNotificationEventFlag[] = [];
  public exposedSafetyEvents: string[] = [];
  public exposedOtherEvents: string[] = [];

  public get exposedEvents(): PortalNotificationEventFlag[] {
    return this._exposedEvents;
  }

  public set exposedEvents(events: PortalNotificationEventFlag[]) {
    this._exposedEvents = events;
    this.exposedSafetyEvents = SAFETY_EVENTS.filter((event) => this._exposedEvents.includes(event));
    this.exposedOtherEvents = OTHER_EVENTS.filter((event) => this._exposedEvents.includes(event));
  }

  constructor(
    private http: HttpClient,
    private cacheService: HttpCacheService,
    private dialog: MatDialog,
    public translate: TranslateService,
    public durationService: DurationRangeService
  ) {}

  private handleError(error: HttpErrorResponse) {
    if (error.status < 500) {
      // A client-side or network error occurred. Handle it accordingly.
      throwError(() => {
        new Error(error.message);
      });
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    return throwError(() => new Error('Something bad happened; please try again later.'));
  }

  getConfiguration(isRefresh: Boolean = true): Observable<Notification_Configuration | HttpResponse<Blob>> {
    if (isRefresh) {
      this.cacheService.burstCache$.next(API.NOTIFICATION_CONFIGURATION);
    }
    return this.http
      .get(`${API.NOTIFICATION_CONFIGURATION}`)
      .pipe(
        map((result: Notification_Configuration) => {
          this.addMissingEventsWithDefaultValues(result);
          return this.sortConfiguration(this.formatConfiguration(result));
        })
      )
      .pipe(catchError(this.handleError));
  }

  postConfiguration(body: any): Observable<any | HttpResponse<Blob>> {
    return this.http.post(API.NOTIFICATION_CONFIGURATION, body).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.NOTIFICATION_CONFIGURATION);
      })
    );
  }

  //add default values for exposed events
  private addMissingEventsWithDefaultValues(result: Notification_Configuration) {
    this.exposedEvents.forEach((event) => {
      const index = result.configurations.findIndex((config) => config.notificationType === event);
      if (index === -1) {
        const defaultValue = EVENTS_DEFAULT_VALUES[event];
        result.configurations.push({ ...defaultValue, notificationType: event });
      }
    });
  }

  private formatConfiguration(config) {
    config.configurations = config.configurations
      .filter((configuration) => {
        const { notificationType } = configuration;
        const configType = INPUT_KEYS[notificationType];
        if (!configType || !configType?.exposeEvent || configType?.readonly) {
          return false;
        }
        return true;
      })
      .map((configuration) => {
        const { userPreferences, userMetadata, tagIds, ..._config } = configuration;
        return {
          ...(tagIds && { tagIds }),
          ..._config,
        };
      });
    return config;
  }

  private sortConfiguration(config) {
    config.configurations = config.configurations.sort((a, b) => {
      return ORDER_NOTIFICATION_CONFIGURATION.indexOf(a.notificationType) - ORDER_NOTIFICATION_CONFIGURATION.indexOf(b.notificationType);
    });
    return config;
  }

  public openSSOConsentDialog() {
    let time = new Date();
    const consentTimeStamp = time.toISOString();

    const dialogRef = this.dialog.open(SsoDialogComponent, {
      position: { top: '24px' },
      width: '360px',
      height: '225px',
      data: {
        consentTime: consentTimeStamp,
        title: this.translate.instant('consentTitleForPn'),
        content: this.translate.instant('consentDialogDescriptionForPn'),
      },
    });
    return dialogRef;
  }

  public getFleetSdkConfigurations(): Observable<any> {
    return this.http.get(API.GET_FLEET_CONFIGURATION);
  }

  // get default, min values for speed, duration and severity from fleet configuration
  // consider values from duty-type in order [heavy, medium, light] only if event is enabled
  // mark as readonly if event is disabled in all duty types
  // please update BE team if default value calculation logic is changed here
  public addDefaultValuesFromFleetConfig(): Observable<any> {
    const DUTY_TYPES = ['heavy', 'medium', 'light'];

    return this.getFleetSdkConfigurations().pipe(
      tap((res) => {
        this.exposedSafetyEvents.forEach((event) => {
          if (INPUT_KEYS[event]) {
            const enabledKey = INPUT_KEYS[event].enabledKey;
            const isEnabled = DUTY_TYPES.map((dutyType) => {
              const isEnabled = !!res[dutyType]?.[enabledKey];
              if (isEnabled && !INPUT_KEYS[event]['dutyType']) {
                INPUT_KEYS[event]['dutyType'] = dutyType;
              }
              return isEnabled;
            });

            INPUT_KEYS[event]['readonly'] = !isEnabled.some((e) => !!e);

            INPUT_KEYS[event].allowedControls?.forEach((control) => {
              const configKey = INPUT_KEYS[event][control]?.configKey;
              if (configKey) {
                const dutyType = INPUT_KEYS[event]['dutyType'] || 'heavy';

                const configValue = Array.isArray(configKey)
                  ? configKey.reduce((value, key) => Math.min(res[dutyType]?.[key], value), 0)
                  : res[dutyType]?.[configKey] || 0;

                if (control === 'severity') {
                  const roundedValue = this.roundDecimal(configValue);
                  INPUT_KEYS[event][control].max = roundedValue;
                  const min = INPUT_KEYS[event][control].min;
                  const diff = this.roundDecimal(0.2 * (roundedValue - min));
                  const defaultValue = this.roundDecimal(roundedValue - diff);
                  INPUT_KEYS[event][control].defaultValue = defaultValue >= min ? defaultValue : min;
                } else {
                  const roundedValue = this.roundDecimal(configValue);
                  INPUT_KEYS[event][control].min = roundedValue;
                  const max = INPUT_KEYS[event][control].max;
                  const diff = this.roundDecimal(0.2 * (max - roundedValue));
                  const defaultValue = this.roundDecimal(roundedValue + diff);
                  INPUT_KEYS[event][control].defaultValue = defaultValue <= max ? defaultValue : max;
                }
              }
            });
          }
        });

        const exposedDistractionEvents = DISTRACTED_EVENTS.filter((event) => this.exposedSafetyEvents.includes(event));
        const isReadonly = exposedDistractionEvents.map((event) => {
          return !!INPUT_KEYS[event]?.['readonly'];
        });
        INPUT_KEYS['DistractedDrivingGroupKey']['readonly'] = isReadonly.every((e) => !!e);
      }),
      catchError(() => of(''))
    );
  }

  // list of events to be exposed on the UI - based on flags set in client config - dynamoDB
  public markExposedEvents() {
    const flags: PortalNotificationEventFlag[] = this.durationService.portalNotificationEventFlags || [];
    const availableEvents = Object.keys(INPUT_KEYS);

    this.exposedEvents = flags.filter((flag) => availableEvents.includes(flag));

    availableEvents.forEach((key) => {
      const isDiagnosticEvent = DIAGNOSTIC_EVENTS.includes(key) || false;
      const exposeEvent = (flags as string[])?.includes(key) || false;
      INPUT_KEYS[key] && (INPUT_KEYS[key].exposeEvent = isDiagnosticEvent || exposeEvent || false);
    });
  }

  public roundDecimal(val, dec = 2) {
    return parseFloat(val.toFixed(dec));
  }
}
